import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { modelService } from "../service/apiService";
import { Model } from "../types";
import { boolean } from "zod";
import { useUser } from "./UserContext";
import { isVoiceModel, FALLBACK_VOICE_MODEL_ID } from "../utils/NovaVoiceManager";

interface ModelContextType {
  selectedModel: Model | null;
  setSelectedModel: React.Dispatch<React.SetStateAction<Model | null>>;
  models: Model[];
  loading: boolean;
  error: string | null;
  fetchAndSetModels: () => Promise<void>;
  toggleModelAvailability: (modelId: string) => Promise<void>;
  isModelEnabled: (modelId: string) => boolean;
  getSelectedModelCapabilities: () => { tool_call_support: boolean; file_upload_support: boolean };
  setModelAsDefault: (modelId: string) => Promise<void>;
  getDefaultModel: () => Model | null;
  isDefaultModel: (modelId: string) => boolean;
  setModelAsVoiceDefault: (modelId: string) => Promise<void>;
  getVoiceModel: () => Model | null;
  setModelCostTier: (modelId: string, costTier: number) => Promise<void>;
  /** Effort for the selected model, or null when the model takes none. */
  effort: string | null;
  setEffort: (effort: string) => void;
}

// The user's effort pick per model, remembered in this browser.
const EFFORT_STORAGE_KEY = "kyva.effortByModel";

const readEffortChoices = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(EFFORT_STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

export const ModelContext = createContext<ModelContextType | undefined>(undefined);

interface ModelProviderProps {
  children: React.ReactNode;
  isAdmin?: boolean;
}

export const ModelProvider: React.FC<ModelProviderProps> = ({ 
  children, 
  isAdmin = boolean
}) => {
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { userId } = useUser();

  const fetchAndSetModels = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const fetchedModels = await modelService.getAllModels();
      setModels(fetchedModels);
      
      if (!selectedModel) {
        const defaultModel = fetchedModels.find((model: Model) => model.default && model.is_enabled);
        // Skip voice models: landing on one would open voice mode on load
        const firstEnabledModel = fetchedModels.find((model: Model) => model.is_enabled && !isVoiceModel(model));
        setSelectedModel(defaultModel || firstEnabledModel || null);
      }
    } catch (err: any) {
      const errorMessage = err.response?.status === 401 
        ? "Unauthorized access. Please log in again."
        : "Failed to fetch models.";
      setError(errorMessage);
      console.error("Error fetching models:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetModels();
  }, [userId]);

  const toggleModelAvailability = async (modelId: string) => {
    if (!isAdmin) {
      setError("Only administrators can modify model availability.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const enabledModels = models
        .filter(model => 
          model.is_enabled || model.foundation_model_id === modelId
        )
        .map(model => model.foundation_model_id);

      const updatedModelIds = models
        .find(m => m.foundation_model_id === modelId)?.is_enabled
        ? enabledModels.filter(id => id !== modelId)
        : [...enabledModels, modelId];

      await modelService.updateEnabledModels(updatedModelIds);
      await fetchAndSetModels();

      if (selectedModel?.foundation_model_id === modelId) {
        const updatedSelectedModel = models.find(
          model => model.is_enabled && model.foundation_model_id !== modelId
        );
        setSelectedModel(updatedSelectedModel || null);
      }
    } catch (err: any) {
      const errorMessage = err.response?.status === 401 
        ? "Unauthorized. Please log in again."
        : "Failed to update model availability.";
      setError(errorMessage);
      console.error("Error toggling model availability:", err);
    } finally {
      setLoading(false);
    }
  };

  // Set model as default
  const setModelAsDefault = async (modelId: string) => {
    if (!isAdmin) {
      setError("Only administrators can set default models.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await modelService.setDefaultModel(modelId);
      
      // Update local state to reflect the change
      setModels(prevModels => 
        prevModels.map(model => ({
          ...model,
          default: model.foundation_model_id === modelId
        }))
      );

      // Show success message (you can customize this)
      console.log(`Model ${modelId} set as default successfully`);
      
    } catch (err: any) {
      const errorMessage = err.response?.status === 401 
        ? "Unauthorized. Please log in again."
        : err.response?.status === 403
        ? "You don't have permission to set default models."
        : "Failed to set model as default.";
      setError(errorMessage);
      console.error("Error setting default model:", err);
    } finally {
      setLoading(false);
    }
  };

  // Set the model voice mode switches to (voice models only)
  const setModelAsVoiceDefault = async (modelId: string) => {
    if (!isAdmin) {
      setError("Only administrators can set the default voice model.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await modelService.setDefaultVoiceModel(modelId);

      setModels(prevModels =>
        prevModels.map(model => ({
          ...model,
          default_voice: model.foundation_model_id === modelId
        }))
      );
    } catch (err: any) {
      const errorMessage = err.response?.status === 401
        ? "Unauthorized. Please log in again."
        : err.response?.status === 403
        ? "You don't have permission to set the default voice model."
        : "Failed to set default voice model.";
      setError(errorMessage);
      console.error("Error setting default voice model:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Set a model's cost tier (1 = economy .. 3 = premium). Drives automatic
  // model selection for Virtual Assistants.
  const setModelCostTier = async (modelId: string, costTier: number) => {
    if (!isAdmin) {
      setError("Only administrators can set model cost tiers.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await modelService.setModelCostTier(modelId, costTier);

      setModels(prevModels =>
        prevModels.map(model =>
          model.foundation_model_id === modelId
            ? { ...model, cost_tier: costTier }
            : model
        )
      );
    } catch (err: any) {
      const errorMessage = err.response?.status === 401
        ? "Unauthorized. Please log in again."
        : err.response?.status === 403
        ? "You don't have permission to set model cost tiers."
        : "Failed to set model cost tier.";
      setError(errorMessage);
      console.error("Error setting model cost tier:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get current default model
  const getDefaultModel = (): Model | null => {
    return models.find(model => model.default) || null;
  };

  // Check if a model is the default
  const isDefaultModel = (modelId: string): boolean => {
    return models.some(model => 
      model.foundation_model_id === modelId && model.default
    );
  };

  // Model voice mode switches to: the admin's voice default, else the fallback, else any enabled voice model
  const getVoiceModel = (): Model | null => {
    const enabledVoiceModels = models.filter(model => model.is_enabled && isVoiceModel(model));
    return enabledVoiceModels.find(model => model.default_voice)
      || enabledVoiceModels.find(model => model.foundation_model_id === FALLBACK_VOICE_MODEL_ID)
      || enabledVoiceModels[0]
      || null;
  };

  const isModelEnabled = (modelId: string): boolean => {
    return models.some(
      model => model.foundation_model_id === modelId && model.is_enabled
    );
  };

  const [effortChoices, setEffortChoices] = useState<Record<string, string>>(readEffortChoices);

  // The saved pick if the model still offers it, else the model's default.
  const supportedEfforts = selectedModel?.supported_efforts || [];
  const savedEffort = selectedModel ? effortChoices[selectedModel.foundation_model_id] : undefined;
  const effort = supportedEfforts.length
    ? (savedEffort && supportedEfforts.includes(savedEffort) ? savedEffort : selectedModel?.default_effort || null)
    : null;

  const setEffort = useCallback((value: string) => {
    if (!selectedModel) return;
    setEffortChoices(prev => {
      const next = { ...prev, [selectedModel.foundation_model_id]: value };
      try {
        localStorage.setItem(EFFORT_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable (private window): the pick still holds for this visit.
      }
      return next;
    });
  }, [selectedModel]);

  const getSelectedModelCapabilities = useCallback(() => {
    if (!selectedModel) {
      return {
        tool_call_support: false,
        file_upload_support: false
      };
    }
    
    return {
      tool_call_support: selectedModel.tool_call_support || false,
      file_upload_support: selectedModel.file_upload_support || false
    };
  }, [selectedModel]);

  return (
    <ModelContext.Provider
      value={{
        selectedModel,
        setSelectedModel,
        models,
        loading,
        error,
        fetchAndSetModels,
        toggleModelAvailability,
        isModelEnabled,
        getSelectedModelCapabilities,
        setModelAsDefault,
        getDefaultModel,
        isDefaultModel,
        setModelAsVoiceDefault,
        getVoiceModel,
        setModelCostTier,
        effort,
        setEffort,
      }}
    >
      {children}
    </ModelContext.Provider>
  );
};

export const useModel = () => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error("useModel must be used within a ModelProvider");
  }
  return context;
};